import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { 
  Package, 
  Search, 
  Eye, 
  Clock, 
  Truck, 
  CheckCircle2, 
  XCircle, 
  RefreshCw, 
  MapPin, 
  X, 
  CreditCard,
  BadgeCheck
} from 'lucide-react';
import apiService from '../../components/services/apiService';
import { useToast } from '../../context/ToastContext';
import { formatVND, formatDateTime } from '../../utils/formatters';

export default function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  
  // State quản lý Modal xem chi tiết & cập nhật đơn hàng
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  // States phân trang (Pagination) giống ProductsAdmin
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;

  const { showToast } = useToast();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let res;
      try {
        res = await apiService.get('/orders/admin/all');
      } catch (err) {
        if (err.response?.status === 404) {
          res = await apiService.get('/orders');
        } else {
          throw err;
        }
      }

      const data = res.data?.data || res.data || [];
      // Sắp xếp đơn hàng mới nhất lên đầu (dựa vào createdAt hoặc id)
      const sortedOrders = Array.isArray(data) ? data.sort((a, b) => {
        // Ưu tiên so sánh ngày tạo (mới nhất lên trước)
        if (a.createdAt && b.createdAt) {
          return new Date(b.createdAt) - new Date(new Date(a.createdAt));
        }
        // Fallback so sánh theo ID giảm dần nếu không có ngày tạo
        return (b.id || 0) - (a.id || 0);
      }) : [];

      setOrders(sortedOrders);
    } catch (error) {
      console.error('Lỗi khi tải danh sách đơn hàng:', error);
      const msg = error.response?.data?.message || 'Không thể kết nối đến máy chủ lấy danh sách đơn hàng';
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Cập nhật trạng thái đơn hàng (CONFIRMED, SHIPPING, COMPLETED, CANCELLED) dùng SweetAlert2
  const handleUpdateStatus = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      await apiService.put(`/orders/admin/${orderId}/status`, { status: newStatus });
      
      // Hiển thị thông báo SweetAlert2 giống trang Product
      Swal.fire({
        title: 'Thành công!',
        text: `Đã chuyển trạng thái đơn hàng sang: ${newStatus}`,
        icon: 'success',
        confirmButtonColor: '#4f46e5',
        background: '#0f172a',
        color: '#ffffff'
      });

      setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, status: newStatus } : o)));
      
      // Đóng modal chi tiết và reset đơn hàng được chọn sau khi thao tác thành công
      setIsDetailOpen(false);
      setSelectedOrder(null);

    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái đơn hàng:', error);
      const msg = error.response?.data?.message || 'Cập nhật trạng thái thất bại';
      Swal.fire({
        title: 'Thất bại!',
        text: msg,
        icon: 'error',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
    } finally {
      setUpdatingId(null);
    }
  };

  // Cập nhật trạng thái thanh toán (unpaid <-> paid)
  const handleUpdatePaymentStatus = async (orderId, newPaymentStatus) => {
    try {
      await apiService.put(`/orders/admin/${orderId}/payment-status`, { paymentStatus: newPaymentStatus });
      
      Swal.fire({
        title: 'Thành công!',
        text: `Đã chuyển trạng thái thanh toán sang: ${newPaymentStatus === 'paid' ? 'Đã thanh toán' : 'Chưa thanh toán'}`,
        icon: 'success',
        confirmButtonColor: '#4f46e5',
        background: '#0f172a',
        color: '#ffffff'
      });

      setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, paymentStatus: newPaymentStatus } : o)));
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, paymentStatus: newPaymentStatus }));
      }
    } catch (error) {
      console.error('Lỗi khi cập nhật thanh toán:', error);
      const msg = error.response?.data?.message || 'Cập nhật trạng thái thanh toán thất bại';
      Swal.fire({
        title: 'Thất bại!',
        text: msg,
        icon: 'error',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
    }
  };

  // Helper hiển thị Badge trạng thái
  const renderStatusBadge = (status, paymentStatus, paymentMethod) => {
    const s = String(status || '').toUpperCase();
    const isPaid = String(paymentStatus || '').toLowerCase() === 'paid';
    const isVnPay = String(paymentMethod || '').toUpperCase() === 'VNPAY';

    switch (s) {
      case 'PENDING':
      case 'CHỜ XỬ LÝ':
      case 'CHỜ XÁC NHẬN':
      case 'PROCESSING':
      case 'ĐANG XỬ LÝ':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
            <Clock className="w-3 h-3" /> {(isPaid || isVnPay) ? 'Đang xác nhận đơn' : 'Chờ xử lý'}
          </span>
        );
      case 'CONFIRMED':
      case 'ĐÃ XÁC NHẬN':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-violet-500/10 text-violet-400 border border-violet-500/20">
            <BadgeCheck className="w-3 h-3" /> Đã xác nhận
          </span>
        );
      case 'SHIPPING':
      case 'ĐANG GIAO':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20">
            <Truck className="w-3 h-3" /> Đang giao
          </span>
        );
      case 'COMPLETED':
      case 'HOÀN THÀNH':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <CheckCircle2 className="w-3 h-3" /> Hoàn thành
          </span>
        );
      case 'CANCELLED':
      case 'ĐÃ HỦY':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-rose-500/10 text-rose-400 border border-rose-500/20">
            <XCircle className="w-3 h-3" /> Đã hủy
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-slate-300">
            {status || 'Đang xác nhận'}
          </span>
        );
    }
  };

  // Lọc danh sách đơn hàng theo trạng thái & từ khóa tìm kiếm
  const filteredOrders = orders.filter((order) => {
    const s = String(order.status || '').toUpperCase();
    const matchStatus = filterStatus === 'ALL' || s.includes(filterStatus);

    const term = searchTerm.toLowerCase();
    const code = String(order.orderCode || order.id || '').toLowerCase();
    const address = String(order.shippingAaddress || order.shippingAddress || '').toLowerCase();
    const userEmail = String(order.user?.email || '').toLowerCase();
    const matchSearch = code.includes(term) || address.includes(term) || userEmail.includes(term);

    return matchStatus && matchSearch;
  });

  // Tính toán phân trang
  const totalPages = Math.ceil(filteredOrders.length / pageSize);
  const paginatedOrders = filteredOrders.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  // Reset trang về 0 khi đổi bộ lọc hoặc tìm kiếm
  useEffect(() => {
    setCurrentPage(0);
  }, [filterStatus, searchTerm]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Tính toán nhanh số liệu thống kê
  const stats = {
    total: orders.length,
    pending: orders.filter(o => String(o.status || '').toUpperCase().includes('PENDING')).length,
    confirmed: orders.filter(o => String(o.status || '').toUpperCase() === 'CONFIRMED').length,
    shipping: orders.filter(o => String(o.status || '').toUpperCase().includes('SHIPPING')).length,
    completed: orders.filter(o => String(o.status || '').toUpperCase().includes('COMPLETED')).length,
    cancelled: orders.filter(o => String(o.status || '').toUpperCase().includes('CANCELLED')).length,
    totalRevenue: orders
      .filter(o => String(o.status || '').toUpperCase().includes('COMPLETED'))
      .reduce((acc, curr) => acc + Number(curr.finalAmount || curr.totalAmount || 0), 0)
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Tiêu đề & Nút Refresh đồng bộ kiểu ProductsAdmin */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-900/60 p-6 rounded-3xl border border-slate-800/80 backdrop-blur-xl">
        <div>
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-400 bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-500/20">
            Quản trị hệ thống
          </span>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-serif text-white mt-2">
            Quản Lý Đơn Hàng
          </h1>
        </div>
        <button
          onClick={fetchOrders}
          className="bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-700/80 px-4 py-3 rounded-2xl text-xs font-bold transition flex items-center gap-2 cursor-pointer shadow-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Làm mới danh sách
        </button>
      </div>

      {/* Thẻ KPIs thống kê đồng bộ giao diện */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-3xl shadow-xl">
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">Tổng số đơn</span>
          <p className="text-2xl font-extrabold text-white mt-1">{stats.total}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-3xl shadow-xl">
          <span className="text-[11px] text-amber-400 font-bold uppercase tracking-wider">Chờ xử lý</span>
          <p className="text-2xl font-extrabold text-amber-400 mt-1">{stats.pending}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-3xl shadow-xl">
          <span className="text-[11px] text-violet-400 font-bold uppercase tracking-wider">Đã xác nhận</span>
          <p className="text-2xl font-extrabold text-violet-400 mt-1">{stats.confirmed}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-3xl shadow-xl">
          <span className="text-[11px] text-blue-400 font-bold uppercase tracking-wider">Đang giao hàng</span>
          <p className="text-2xl font-extrabold text-blue-400 mt-1">{stats.shipping}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-3xl shadow-xl">
          <span className="text-[11px] text-emerald-400 font-bold uppercase tracking-wider">Đã hoàn thành</span>
          <p className="text-2xl font-extrabold text-emerald-400 mt-1">{stats.completed}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-3xl shadow-xl col-span-2 md:col-span-1">
          <span className="text-[11px] text-indigo-400 font-bold uppercase tracking-wider">Doanh thu hoàn tất</span>
          <p className="text-lg font-extrabold text-indigo-400 mt-1 truncate">
            {formatVND(stats.totalRevenue)}
          </p>
        </div>
      </div>

      {/* Bộ lọc Tabs & Ô tìm kiếm đồng bộ */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
          {[
            { key: 'ALL', label: 'Tất cả' },
            { key: 'PENDING', label: 'Chờ xử lý' },
            { key: 'CONFIRMED', label: 'Đã xác nhận' },
            { key: 'SHIPPING', label: 'Đang giao' },
            { key: 'COMPLETED', label: 'Hoàn thành' },
            { key: 'CANCELLED', label: 'Đã hủy' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilterStatus(tab.key)}
              className={`px-4 py-2 rounded-2xl text-xs font-bold transition cursor-pointer whitespace-nowrap ${
                filterStatus === tab.key
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                  : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative min-w-[280px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Tìm theo mã đơn, địa chỉ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-2xl pl-10 pr-4 py-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition shadow-inner"
          />
        </div>
      </div>

      {/* Bảng danh sách đơn hàng */}
      <div className="bg-slate-900 border border-slate-800/80 rounded-3xl overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950/60 text-slate-400 font-bold border-b border-slate-800 uppercase tracking-wider text-[11px]">
              <tr>
                <th className="py-4 px-5">Mã đơn</th>
                <th className="py-4 px-5">Thời gian đặt</th>
                <th className="py-4 px-5">Địa chỉ giao hàng</th>
                <th className="py-4 px-5">Tổng tiền</th>
                <th className="py-4 px-5">Thanh toán</th>
                <th className="py-4 px-5">Trạng thái</th>
                <th className="py-4 px-5 text-center">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500">
                    <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    Đang tải dữ liệu đơn hàng...
                  </td>
                </tr>
              ) : paginatedOrders.length > 0 ? (
                paginatedOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-slate-800/40 transition">
                    <td className="py-4 px-5 font-bold text-white whitespace-nowrap font-mono">
                      #{order.orderCode || order.id}
                    </td>
                    <td className="py-4 px-5 text-slate-400 whitespace-nowrap">
                      {formatDateTime(order.createdAt)}
                    </td>
                    <td className="py-4 px-5 max-w-xs truncate text-slate-300" title={order.shippingAaddress || order.shippingAddress}>
                      {order.shippingAaddress || order.shippingAddress || 'N/A'}
                    </td>
                    <td className="py-4 px-5 font-bold text-indigo-400 whitespace-nowrap font-mono">
                      {formatVND(order.finalAmount || order.totalAmount || 0)}
                    </td>
                    <td className="py-4 px-5 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] px-2 py-0.5 rounded-lg bg-slate-800 text-slate-300 border border-slate-700 font-semibold">
                          {order.paymentMethod || 'COD'}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-lg font-bold ${
                          order.paymentStatus === 'paid' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        }`}>
                          {order.paymentStatus === 'paid' ? 'Đã trả' : 'Chưa'}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-5 whitespace-nowrap">
                      {renderStatusBadge(order.status, order.paymentStatus, order.paymentMethod)}
                    </td>
                    <td className="py-4 px-5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setIsDetailOpen(true);
                          }}
                          className="p-2.5 bg-slate-800/80 hover:bg-indigo-600 text-slate-300 hover:text-white rounded-2xl transition cursor-pointer shadow-sm"
                          title="Xem chi tiết đơn hàng"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500">
                    Không tìm thấy đơn hàng nào phù hợp với bộ lọc.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* THANH PHÂN TRANG (Pagination) */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-5 border-t border-slate-800 bg-slate-950/40">
            <span className="text-xs text-slate-400 font-medium">
              Trang <span className="font-bold text-white">{currentPage + 1}</span> / <span className="font-bold text-white">{totalPages}</span>
            </span>

            <div className="flex items-center space-x-1.5">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition ${
                  currentPage === 0
                    ? 'border-slate-800 text-slate-600 cursor-not-allowed'
                    : 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white cursor-pointer'
                }`}
              >
                Trước
              </button>

              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => handlePageChange(index)}
                  className={`w-9 h-9 rounded-xl text-xs font-bold border transition ${
                    currentPage === index
                      ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                      : 'border-slate-700/80 text-slate-300 hover:bg-slate-800 hover:text-white cursor-pointer'
                  }`}
                >
                  {index + 1}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages - 1}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition ${
                  currentPage === totalPages - 1
                    ? 'border-slate-800 text-slate-600 cursor-not-allowed'
                    : 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white cursor-pointer'
                }`}
              >
                Sau
              </button>
            </div>
          </div>
        )}
      </div>

      {/* --- MODAL XEM CHI TIẾT & CẬP NHẬT TRẠNG THÁI (ADMIN) --- */}
      {isDetailOpen && selectedOrder && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 space-y-6 shadow-2xl">
            
            {/* Header Modal */}
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-indigo-600/10 text-indigo-400 rounded-2xl border border-indigo-500/20">
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white font-serif">
                    Chi tiết đơn hàng #{selectedOrder.orderCode || selectedOrder.id}
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Đặt lúc: {formatDateTime(selectedOrder.createdAt)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsDetailOpen(false);
                  setSelectedOrder(null);
                }}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-2xl transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Thông tin giao hàng & Thanh toán */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-950 p-4 rounded-2xl border border-slate-800/80 text-xs">
              <div className="space-y-1">
                <span className="text-slate-400 font-semibold flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-indigo-400" /> Địa chỉ giao hàng:
                </span>
                <p className="text-white font-semibold">
                  {selectedOrder.shippingAaddress || selectedOrder.shippingAddress || 'N/A'}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-slate-400 font-semibold flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5 text-indigo-400" /> Thanh toán:
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold">
                    {selectedOrder.paymentMethod || 'COD'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleUpdatePaymentStatus(
                      selectedOrder.id, 
                      selectedOrder.paymentStatus === 'paid' ? 'unpaid' : 'paid'
                    )}
                    className={`px-2.5 py-1 rounded-xl text-[10px] font-bold transition cursor-pointer border ${
                      selectedOrder.paymentStatus === 'paid'
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20'
                    }`}
                  >
                    {selectedOrder.paymentStatus === 'paid' ? 'Đã thanh toán (Click đổi)' : 'Chưa thanh toán (Click xác nhận)'}
                  </button>
                </div>
              </div>
            </div>

            {/* Danh sách mặt hàng */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                Sản phẩm trong đơn ({(selectedOrder.orderItems || selectedOrder.items || []).length})
              </h4>
              <div className="border border-slate-800 rounded-2xl divide-y divide-slate-800/80 overflow-hidden bg-slate-950/40">
                {(selectedOrder.orderItems || selectedOrder.items || []).map((item, idx) => (
                  <div key={idx} className="p-4 flex items-center justify-between text-xs">
                    <div>
                      <p className="font-bold text-white">{item.productName || item.name}</p>
                      <span className="text-slate-400 text-[11px]">Số lượng: x{item.quantity}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-indigo-400 font-mono">
                        {formatVND(item.price)}
                      </p>
                      {item.quantity > 1 && (
                        <p className="text-[10px] text-slate-500 font-mono">
                          Tổng: {formatVND((item.price || 0) * (item.quantity || 1))}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bảng tổng kết tiền */}
            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800/80 space-y-2 text-xs">
              <div className="flex justify-between text-slate-400">
                <span>Tiền hàng:</span>
                <span className="font-mono">{formatVND(selectedOrder.totalAmount || 0)}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Phí vận chuyển:</span>
                <span className="font-mono">{formatVND(selectedOrder.shippingFee || 0)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-white pt-2 border-t border-slate-800">
                <span>Tổng thanh toán:</span>
                <span className="text-emerald-400 text-base font-mono font-extrabold">
                  {formatVND(selectedOrder.finalAmount || selectedOrder.totalAmount || 0)}
                </span>
              </div>
            </div>

            {/* Cập nhật trạng thái đơn hàng */}
            <div className="space-y-3 pt-2 border-t border-slate-800">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-300">
                  Trạng thái hiện tại:
                </span>
                <div>{renderStatusBadge(selectedOrder.status, selectedOrder.paymentStatus, selectedOrder.paymentMethod)}</div>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-bold text-slate-400 block uppercase tracking-wider">
                  Chuyển trạng thái đơn sang:
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={updatingId === selectedOrder.id}
                    onClick={() => handleUpdateStatus(selectedOrder.id, 'CONFIRMED')}
                    className="px-4 py-2.5 bg-violet-600/10 hover:bg-violet-600/20 text-violet-400 border border-violet-500/20 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    <BadgeCheck className="w-3.5 h-3.5" /> Xác nhận đơn
                  </button>
                  <button
                    type="button"
                    disabled={updatingId === selectedOrder.id}
                    onClick={() => handleUpdateStatus(selectedOrder.id, 'SHIPPING')}
                    className="px-4 py-2.5 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 border border-blue-500/20 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    <Truck className="w-3.5 h-3.5" /> Giao hàng
                  </button>
                  <button
                    type="button"
                    disabled={updatingId === selectedOrder.id}
                    onClick={() => handleUpdateStatus(selectedOrder.id, 'COMPLETED')}
                    className="px-4 py-2.5 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 border border-emerald-500/20 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Hoàn thành
                  </button>
                  <button
                    type="button"
                    disabled={updatingId === selectedOrder.id}
                    onClick={() => handleUpdateStatus(selectedOrder.id, 'CANCELLED')}
                    className="px-4 py-2.5 bg-rose-600/10 hover:bg-rose-600/20 text-rose-400 border border-rose-500/20 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    <XCircle className="w-3.5 h-3.5" /> Hủy đơn
                  </button>
                </div>
              </div>
            </div>

            {/* Nút đóng */}
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsDetailOpen(false);
                  setSelectedOrder(null);
                }}
                className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition cursor-pointer"
              >
                Đóng
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}